/* What a place sounds like, as a rule rather than a node graph.
 *
 * The game had no ambience at all: one 64-second loop covered six biomes, day
 * and night, and nothing else made a sound unless you hit it. There was no
 * audible difference between standing in a meadow and standing in an ash
 * field, and none at all between noon and the long night.
 *
 * `trackFor()` cannot fix that — five composed tracks are not six biomes
 * times two times of day, and writing six more is composition, not a change.
 * But most of what a place sounds like is noise, and noise is cheap: a
 * filtered bed whose colour and weight follow the biome, and a brighter voice
 * that rises with how much open water is in front of you.
 *
 * The decision lives here, pure, because the wiring needs an AudioContext and
 * node has none — the same reason `src/labels.ts` exists. `Game.ambience()`
 * builds the nodes and ramps them to whatever this returns.
 */

/** Per biome: the bed's cutoff in Hz, and its gain.
 *  An ash field is a hollow roar, a bloom meadow a soft wide hiss, scree thin
 *  and high, a fen low and close. */
export const BEDS = {
  heath: { freq: 620, gain: 0.030 },
  bloom: { freq: 520, gain: 0.026 },
  fen: { freq: 430, gain: 0.038 },
  dust: { freq: 900, gain: 0.022 },
  scree: { freq: 1250, gain: 0.018 },
  ash: { freq: 300, gain: 0.042 },
};


/** How much water on a screen counts as "a lakeside". A screen is 16x12. */
const FULL_WATER = 60;
/** ...and the loudest the water voice ever gets. Under the music, always. */
const WET_MAX = 0.030;

/**
 * @param {string} biome     which wild the player is standing in
 * @param {boolean} night    the long night
 * @param {number} wetTiles  open water tiles on this screen
 * @returns {{freq:number, bed:number, wet:number}}
 */
export function ambienceFor(biome, night, wetTiles = 0) {
  const b = BEDS[biome] || BEDS.heath;
  // THE NIGHT IS DARKER AND CLOSER, which is the one thing a single looped
  // track could never say. Same bed, filtered down and pushed up.
  //
  // ...AND IT DOES NOT REACH UNDERGROUND. A hollow does not get darker when
  // the sun goes down; it was already dark, and the night term was moving the
  // bed of every room down there by a third of its cutoff on a clock nobody
  // in it can see.
  const n = night ? 1 : 0;
  const wet = Math.max(0, Math.min(1, wetTiles / FULL_WATER)) * WET_MAX;
  return {
    freq: Math.round(b.freq * (1 - 0.35 * n)),
    bed: b.gain * (1 + 0.5 * n),
    wet,
  };
}
